/**
 * Client-side encryption v2 — key wrapping.
 *
 * Fixes the v1 flaws (see docs/encryption-v2-design.md):
 *   - v1 wrapped BOTH the RSA private key and the vault key with the SAME
 *     ChaCha20-Poly1305 key AND the same fixed all-zero nonce, so the two
 *     ciphertexts shared a keystream and the vault key was recoverable from
 *     server-stored data without the passphrase.
 *   - v1 derived the wrap key with unsalted SHA1 -> SHA256 (fast, no salt).
 *
 * v2:
 *   - KDF: Argon2id (hash-wasm) with a random per-user salt.
 *   - AEAD: XChaCha20-Poly1305 (@noble/ciphers) with a fresh random 24-byte
 *     nonce for EVERY secret. A reused (key, nonce) pair is therefore
 *     impossible, and tampering is detected by the Poly1305 tag.
 *
 * The user still enters a user-chosen passphrase (Bitwarden model); only the
 * salt, nonces, and vault key are random. The salt and wrapped blobs live
 * server-side, so any device re-derives the same wrap key from
 * passphrase + salt — nothing is stored on the device.
 *
 * This module only changes KEY WRAPPING. The vault-data cipher
 * (EncryptionService) is unchanged here; the vault key stays a 64-char hex
 * string so EncryptionService keeps working across the v1 -> v2 transition.
 */
import { xchacha20poly1305 } from "@noble/ciphers/chacha";
import { bytesToHex, bytesToUtf8, utf8ToBytes } from "@noble/ciphers/utils";
import { argon2id } from "hash-wasm";
import forge from "node-forge";

// --- format identifiers (self-describing envelope) ---
export const WRAP_VERSION = 2;
const KDF_ID = "a2id"; // Argon2id (passphrase -> wrap key)
const DATA_KDF_ID = "dk"; // data key = the vault key itself (no passphrase KDF)
const ALG_ID = "xc20p"; // XChaCha20-Poly1305
const ENVELOPE_PREFIX = `v${WRAP_VERSION}`;
// Vault-data envelope: v2.dk.xc20p.<nonce>.<ct‖tag>  (5 parts, no salt).
const DATA_ENVELOPE_PREFIX = `${ENVELOPE_PREFIX}.${DATA_KDF_ID}.${ALG_ID}`;

// --- Argon2id parameters. Runs only at setup/unlock, never on a hot path.
// Tune per device class before locking for native (see design doc §8).
export const ARGON2_PARAMS = {
  parallelism: 1,
  iterations: 3,
  memorySize: 65536, // KiB = 64 MiB
  hashLength: 32,
};
const SALT_BYTES = 16;
const NONCE_BYTES = 24; // XChaCha20 nonce

// ---------------------------------------------------------------------------
// byte / encoding helpers (browser + Node, no Buffer dependency)
// ---------------------------------------------------------------------------
export function randomBytes(n) {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return b;
}

/** A fresh random vault key as 64-char hex (used for setup and rotation). */
export function randomVaultKey() {
  return bytesToHex(randomBytes(32));
}

function b64uEncode(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return globalThis
    .btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function b64uDecode(str) {
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const bin = globalThis.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// envelope: v2.a2id.xc20p.<salt>.<nonce>.<ciphertext‖tag>   (all base64url)
// ---------------------------------------------------------------------------
export function isV2Envelope(blob) {
  return typeof blob === "string" && blob.startsWith(`${ENVELOPE_PREFIX}.`);
}

function encodeEnvelope({ salt, nonce, ct }) {
  return [
    ENVELOPE_PREFIX,
    KDF_ID,
    ALG_ID,
    b64uEncode(salt),
    b64uEncode(nonce),
    b64uEncode(ct),
  ].join(".");
}

function decodeEnvelope(str) {
  if (!isV2Envelope(str)) throw new Error("not a v2 envelope");
  const parts = str.split(".");
  if (parts.length !== 6) throw new Error("malformed v2 envelope");
  const [, kdf, alg, salt, nonce, ct] = parts;
  if (kdf !== KDF_ID) throw new Error(`unsupported kdf: ${kdf}`);
  if (alg !== ALG_ID) throw new Error(`unsupported alg: ${alg}`);
  return {
    salt: b64uDecode(salt),
    nonce: b64uDecode(nonce),
    ct: b64uDecode(ct),
  };
}

// ---------------------------------------------------------------------------
// core crypto
// ---------------------------------------------------------------------------
/** Derive a 32-byte wrap key from the passphrase and a salt via Argon2id. */
export async function deriveWrapKey(passphrase, salt) {
  return await argon2id({
    password: utf8ToBytes(passphrase),
    salt,
    ...ARGON2_PARAMS,
    outputType: "binary",
  });
}

/** Seal plaintext bytes under a wrap key; embeds the KDF salt + a fresh nonce. */
function sealSecret(wrapKey, salt, plaintextBytes) {
  const nonce = randomBytes(NONCE_BYTES);
  const ct = xchacha20poly1305(wrapKey, nonce).encrypt(plaintextBytes);
  return encodeEnvelope({ salt, nonce, ct });
}

/** Open a v2 envelope with a wrap key. Throws on wrong key or tampering. */
function openSecret(wrapKey, envelope) {
  const { nonce, ct } = decodeEnvelope(envelope);
  return xchacha20poly1305(wrapKey, nonce).decrypt(ct);
}

/** Read the KDF salt embedded in a v2 envelope (same salt across a user's blobs). */
function saltOf(envelope) {
  return decodeEnvelope(envelope).salt;
}

// ---------------------------------------------------------------------------
// vault-DATA AEAD (field values + files)
//
// Unlike the wrapping envelope above, vault data is keyed directly by the
// 32-byte data key the caller derives from the vault key — there is no
// passphrase/KDF here, so the envelope carries no salt. These are synchronous
// (no Argon2) so EncryptionService.encrypt/decrypt stay synchronous. The data
// key is a Uint8Array(32); EncryptionService derives it (SHA-256 of the vault
// key) and owns that step so this module keeps no crypto-js dependency.
//
//   envelope: v2.dk.xc20p.<gen>.<nonce>.<ciphertext‖tag>   (all base64url)
//
// <gen> is the vault-key GENERATION that encrypted this blob (for Tier B
// rotation). Each rotation mints a new vault key at generation g+1; data stays
// tagged with the generation that wrote it, so a partially-rotated vault is
// still readable (the keyring holds every live generation's key). Step-1 blobs
// predate this and are the 5-part form (no <gen>) — read as generation 1.
// ---------------------------------------------------------------------------
export const BASE_KEY_GENERATION = 1;

export function isV2DataEnvelope(blob) {
  return (
    typeof blob === "string" && blob.startsWith(`${DATA_ENVELOPE_PREFIX}.`)
  );
}

/** The vault-key generation a v2 data envelope was sealed under (1 if untagged). */
export function dataEnvelopeGeneration(envelope) {
  if (!isV2DataEnvelope(envelope)) throw new Error("not a v2 data envelope");
  const parts = envelope.split(".");
  if (parts.length === 5) return BASE_KEY_GENERATION; // step-1 untagged blob
  if (parts.length === 6) {
    const gen = Number(parts[3]);
    if (!Number.isInteger(gen) || gen < 1) throw new Error("bad generation");
    return gen;
  }
  throw new Error("malformed v2 data envelope");
}

/**
 * Encrypt a plaintext string to a v2 data envelope under a 32-byte data key,
 * tagged with the given vault-key generation.
 */
export function sealData(dataKey, plaintext, generation = BASE_KEY_GENERATION) {
  const nonce = randomBytes(NONCE_BYTES);
  const ct = xchacha20poly1305(dataKey, nonce).encrypt(utf8ToBytes(plaintext));
  return [
    ENVELOPE_PREFIX,
    DATA_KDF_ID,
    ALG_ID,
    String(generation),
    b64uEncode(nonce),
    b64uEncode(ct),
  ].join(".");
}

/**
 * Decrypt a v2 data envelope (5-part step-1 or 6-part generation-tagged) with
 * the matching data key. Throws on a wrong key or tampering (Poly1305). The
 * caller is responsible for passing the key for this blob's generation
 * (see dataEnvelopeGeneration).
 */
export function openData(dataKey, envelope) {
  if (!isV2DataEnvelope(envelope)) throw new Error("not a v2 data envelope");
  const parts = envelope.split(".");
  let kdf, alg, nonce, ct;
  if (parts.length === 5) {
    [, kdf, alg, nonce, ct] = parts; // untagged step-1 blob
  } else if (parts.length === 6) {
    [, kdf, alg, , nonce, ct] = parts; // skip the generation field
  } else {
    throw new Error("malformed v2 data envelope");
  }
  if (kdf !== DATA_KDF_ID) throw new Error(`unsupported data kdf: ${kdf}`);
  if (alg !== ALG_ID) throw new Error(`unsupported alg: ${alg}`);
  return bytesToUtf8(
    xchacha20poly1305(dataKey, b64uDecode(nonce)).decrypt(b64uDecode(ct))
  );
}

// ---------------------------------------------------------------------------
// agent (executor) handoff — wrap the vault key with an agent's RSA public key
//
// The owner wraps the current vault key with the ACCEPTED agent's RSA public
// key (RSA-OAEP / SHA-256). Only that agent's private key (unlocked by their
// own passphrase) can open it — the server stores an opaque blob and can never
// read the vault key. Self-describing envelope so the agent side can detect it:
//
//   v2.rsa.oaep.<base64url(ciphertext)>
//
// RSA-2048 OAEP/SHA-256 fits ~190 bytes of plaintext; the vault key is a 64- or
// 128-char hex string, well within that. See docs/encryption-v2-design.md §5.
// ---------------------------------------------------------------------------
const AGENT_WRAP_PREFIX = `${ENVELOPE_PREFIX}.rsa.oaep`;

export function isAgentWrapEnvelope(blob) {
  return typeof blob === "string" && blob.startsWith(`${AGENT_WRAP_PREFIX}.`);
}

/** Wrap a vault key string with an agent's RSA public key (PEM). */
export function wrapKeyForAgent(agentPublicKeyPem, vaultKey) {
  const pub = forge.pki.publicKeyFromPem(agentPublicKeyPem);
  const ct = pub.encrypt(forge.util.encodeUtf8(vaultKey), "RSA-OAEP", {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  });
  return `${AGENT_WRAP_PREFIX}.${b64uEncode(
    Uint8Array.from(ct, (c) => c.charCodeAt(0))
  )}`;
}

/** Unwrap a vault key the owner wrapped for this agent, using the agent's RSA private key (PEM). */
export function unwrapKeyAsAgent(rsaPrivateKeyPem, envelope) {
  if (!isAgentWrapEnvelope(envelope)) throw new Error("not an agent-wrap envelope");
  const b64u = envelope.split(".")[3];
  const bytes = b64uDecode(b64u);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const priv = forge.pki.privateKeyFromPem(rsaPrivateKeyPem);
  const pt = priv.decrypt(bin, "RSA-OAEP", {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  });
  return forge.util.decodeUtf8(pt);
}

// ---------------------------------------------------------------------------
// high-level passphrase operations — same contract as common.js v1
// ---------------------------------------------------------------------------
/**
 * Set up fresh v2 encryption material for a new passphrase.
 * Returns the same shape as the v1 setUpEncryptionWithPassphrase so callers
 * (CreatePassphraseDialog, MainLayout, SecurityMain) need no changes.
 */
export async function setupEncryptionV2(passphrase = "") {
  const salt = randomBytes(SALT_BYTES);
  const wrapKey = await deriveWrapKey(passphrase, salt);

  const keypair = forge.pki.rsa.generateKeyPair({ bits: 2048 });
  const rsaPrivateKey = forge.pki.privateKeyToPem(keypair.privateKey);
  const rsaPublicKey = forge.pki.publicKeyToPem(keypair.publicKey);

  // Vault key stays a 64-char hex (32 bytes) for EncryptionService compatibility.
  const encryptionKey = bytesToHex(randomBytes(32));

  // One KDF, same salt, but each secret gets its own random nonce.
  const encryptedPrivateKey = sealSecret(
    wrapKey,
    salt,
    utf8ToBytes(rsaPrivateKey)
  );
  const encryptedEncryptionKey = sealSecret(
    wrapKey,
    salt,
    utf8ToBytes(encryptionKey)
  );

  return {
    rsaPrivateKey,
    rsaPublicKey,
    encryptionKey,
    encryptedEncryptionKey,
    encryptedPrivateKey,
    wrapVersion: WRAP_VERSION,
  };
}

/**
 * Unwrap the vault key (and optionally the RSA private key) from v2 blobs.
 * @returns {Promise<{valid:boolean, encryptionKey?:string, rsaPrivateKey?:string}>}
 */
export async function unwrapV2(
  passphrase,
  encryptedEncryptionKey,
  encryptedPrivateKey = null
) {
  try {
    const salt = saltOf(encryptedEncryptionKey);
    const wrapKey = await deriveWrapKey(passphrase, salt);
    const encryptionKey = bytesToUtf8(openSecret(wrapKey, encryptedEncryptionKey));
    const out = { valid: true, encryptionKey };
    if (encryptedPrivateKey) {
      out.rsaPrivateKey = bytesToUtf8(openSecret(wrapKey, encryptedPrivateKey));
    }
    return out;
  } catch {
    // Wrong passphrase or tampered ciphertext — AEAD tag fails to verify.
    return { valid: false };
  }
}

/**
 * Re-wrap an EXISTING vault key + RSA keypair under a fresh v2 salt/nonces.
 * Used to migrate a v1 account to v2 without changing the vault key (Tier A:
 * stops all future-snapshot leakage; does not rotate the vault key itself).
 */
export async function rewrapToV2({
  passphrase,
  encryptionKey,
  rsaPrivateKey,
  rsaPublicKey,
}) {
  const salt = randomBytes(SALT_BYTES);
  const wrapKey = await deriveWrapKey(passphrase, salt);
  return {
    rsaPrivateKey,
    rsaPublicKey,
    encryptionKey,
    encryptedPrivateKey: sealSecret(wrapKey, salt, utf8ToBytes(rsaPrivateKey)),
    encryptedEncryptionKey: sealSecret(
      wrapKey,
      salt,
      utf8ToBytes(encryptionKey)
    ),
    wrapVersion: WRAP_VERSION,
  };
}
